import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import {
  AdapterOptions,
  FlatAnswerMutation,
  SectionAdapter
} from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'

/**
 * Gastrointestinal subsection adapter
 * Handles the most complex GI/GU assessment with feeding tubes, catheters, nutrition (19 fields)
 * This is the most complex subsection with many interdependent fields
 */
export class GastrointestinalAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // GI assessment
    const giAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT
    )

    const bmDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE
    )?.at(0)

    // GU assessment
    const guAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT
    )

    // Catheter assessment
    const catheter = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER
    )?.at(0)

    const catheterType = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE
    )

    // Feeding tube assessment - complex with many related fields
    const feedingTube = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE
    )?.at(0)

    const feedingTubeAmount = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT
    )?.at(0)

    const feedingTotalNumber = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER
    )?.at(0)

    const feedingTubeDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION
    )?.at(0)

    const feedingTubeType = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE
    )?.at(0)

    const feedingTubeSite = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE
    )

    const feedingTubeSize = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE
    )?.at(0)

    const feedingTubeNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES
    )?.at(0)

    // Nutrition assessment
    const nutritionWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL
    )?.at(0)

    const nutritionalFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS
    )

    const dietRequirements = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS
    )?.at(0)

    const nutritionNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES
    )?.at(0)

    const nutritionalScreening = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING
    )

    const historyNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.GASTROINTESTINAL_GI_ASSESSMENT]: giAssessment,
      [RecertFormField.GASTROINTESTINAL_BM_DATE]: bmDate,
      [RecertFormField.GASTROINTESTINAL_GU_ASSESSMENT]: guAssessment,
      [RecertFormField.GASTROINTESTINAL_CATHETER]: catheter,
      [RecertFormField.GASTROINTESTINAL_CATHETER_TYPE]: catheterType,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TUBE]: feedingTube,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT]: feedingTubeAmount as number,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER]: feedingTotalNumber as number,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION]: feedingTubeDuration,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE]: feedingTubeType,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE]: feedingTubeSite,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE]: feedingTubeSize,
      [RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES]: feedingTubeNotes,
      [RecertFormField.GASTROINTESTINAL_NUTRITION_WNL]: nutritionWnl,
      [RecertFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS]: nutritionalFactors,
      [RecertFormField.GASTROINTESTINAL_DIET_REQUIREMENTS]: dietRequirements,
      [RecertFormField.GASTROINTESTINAL_NUTRITION_NOTES]: nutritionNotes,
      [RecertFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING]: nutritionalScreening,
      [RecertFormField.GASTROINTESTINAL_HISTORY_NOTES]: historyNotes,
      [RecertFormField.GASTROINTESTINAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(formData: RecertFormData): FlatAnswerMutation[] {
    return [
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT,
        value: formData[RecertFormField.GASTROINTESTINAL_GI_ASSESSMENT]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE,
        value: formData[RecertFormField.GASTROINTESTINAL_BM_DATE]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT,
        value: formData[RecertFormField.GASTROINTESTINAL_GU_ASSESSMENT]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER,
        value: formData[RecertFormField.GASTROINTESTINAL_CATHETER]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE,
        value: formData[RecertFormField.GASTROINTESTINAL_CATHETER_TYPE]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TUBE]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES,
        value: formData[RecertFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL,
        value: formData[RecertFormField.GASTROINTESTINAL_NUTRITION_WNL]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS,
        value: formData[RecertFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS,
        value: formData[RecertFormField.GASTROINTESTINAL_DIET_REQUIREMENTS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES,
        value: formData[RecertFormField.GASTROINTESTINAL_NUTRITION_NOTES]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING,
        value: formData[RecertFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES,
        value: formData[RecertFormField.GASTROINTESTINAL_HISTORY_NOTES]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY,
        value: formData[RecertFormField.GASTROINTESTINAL_SYSTEM_SUMMARY]
      }
    ]
  }
}
